import React, { useCallback, useState } from 'react';
import ChevronDown from '@uber/base-icons/chevron-down';
import ChevronRight from '@uber/base-icons/chevron-right';
import { useStyletron } from 'baseui';
import { Button, KIND, SHAPE, SIZE } from 'baseui/button';
import { Tag } from 'baseui/tag';
import { getTagColors } from './utils/tag-color-utils';
import { NavigateButton } from './action-primitives';
import { ActionButtonContainer, ExpandedItemContainer, ExpandedItemHeader, ListItemContainer, ListItemContent } from './styled-components';
import type { ItemCardProps } from './types';
export const ItemCard = ({
  item,
  navigationConfig,
  renderItemActions,
  isLast
}: ItemCardProps) => {
  const [css, theme] = useStyletron();
  const [isExpanded, setIsExpanded] = useState(false);
  const isNavigable = navigationConfig.canNavigate(item);
  const hasCustomActions = Boolean(item.customActions);
  const renderedActions = renderItemActions?.(item);
  const hasRenderedActions = Boolean(renderedActions);
  const showNavigateButton = isNavigable && !hasCustomActions && !hasRenderedActions;
  const handleNavigate = useCallback(() => {
    navigationConfig.onNavigate(item);
  }, [navigationConfig, item]);
  const handleExpand = useCallback(() => {
    setIsExpanded(true);
    if (isNavigable && !showNavigateButton) {
      handleNavigate();
    }
  }, [isNavigable, showNavigateButton, handleNavigate]);
  const handleCollapse = useCallback(() => {
    setIsExpanded(false);
  }, []);
  const displayText = item.title || item.description;
  const tagLabel = item.tagLabel || item.label;
  const {
    hierarchy
  } = getTagColors({
    isExpanded
  });
  const tagOverrides = {
    Root: {
      style: {
        margin: 0
      }
    },
    Text: {
      style: {
        maxWidth: 'none'
      }
    }
  };
  return <ListItemContainer $isLast={isLast} $isExpanded={isExpanded}>
      {isExpanded ? <ExpandedItemContainer>
          <ExpandedItemHeader>
            <Tag closeable={false} hierarchy={hierarchy} overrides={tagOverrides}>
              <div className={css({
            ...theme.typography.ParagraphSmall,
            color: 'inherit'
          })}>
                {tagLabel}
              </div>
            </Tag>
            <ActionButtonContainer>
              <Button kind={KIND.tertiary} size={SIZE.mini} shape={SHAPE.circle} onClick={handleCollapse} data-testid="collapse-card-button">
                <ChevronDown height={theme.sizing.scale500} width={theme.sizing.scale500} />
              </Button>
            </ActionButtonContainer>
          </ExpandedItemHeader>
          <div className={css({
        ...theme.typography.ParagraphSmall,
        color: theme.colors.contentSecondary,
        margin: 0
      })}>
            {item.description}
          </div>
          {item.customActions}
          {renderedActions}
          {showNavigateButton && <NavigateButton label={item.objectType} onNavigate={handleNavigate} />}
        </ExpandedItemContainer> : <>
          <ListItemContent>
            <Tag closeable={false} hierarchy={hierarchy} overrides={tagOverrides}>
              <div className={css({
            ...theme.typography.ParagraphSmall,
            color: 'inherit'
          })}>
                {tagLabel}
              </div>
            </Tag>
            <div className={css({
          ...theme.typography.ParagraphSmall,
          color: theme.colors.contentSecondary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        })}>
              {'• '}
              {displayText}
            </div>
          </ListItemContent>
          <Button kind={KIND.tertiary} size={SIZE.mini} shape={SHAPE.circle} onClick={handleExpand} data-testid="expand-card-button">
            <ChevronRight height={theme.sizing.scale500} width={theme.sizing.scale500} />
          </Button>
        </>}
    </ListItemContainer>;
};